import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { OpeningDetailResponse, SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { JobDetailFormState } from '../types';
import { JobCard } from './job-card';
import { RefreshBanner } from './refresh-banner';
import { openingIdsInChannels } from '../utils/channel-utils';
import { useValidateBaseFields } from './hooks/use-validate-base-fields';
import {
  hiringAddressOf,
  HiringAddressValues,
  refreshUntouchedAddress,
} from './hiring-address';
import { SummaryView } from './summary-view';
import {
  contractDef,
  PostingRequirementsDef,
  productDef,
  requirementsKeyOf,
} from './posting-requirements/requirements-key';

// Stable, so useSuspenseQueries memoizes the combined result.
function combineDefs(
  results: { data: PostingRequirementsDef[] }[],
): PostingRequirementsDef[][] {
  return results.map(result => result.data);
}

/**
 * Fields on `JobDetailFormState` that are managed by monolith — refreshed on every
 * successful refetch so the user sees the latest values after editing in monolith.
 * Everything else (`title`, `description`, `stateCode`, `streetAddress`, `isExpanded`)
 * is user-managed and preserved across refetches. A remote opening's `city` and
 * `postalCode` are typed by the recruiter, so they are user-managed too, and a
 * refetch refreshes only the address fields the recruiter left untouched.
 */
function serverManagedFields(
  opening: OpeningDetailResponse,
): Pick<
  JobDetailFormState,
  | 'remote'
  | 'industry'
  | 'jobHours'
  | 'jobType'
  | 'experience'
  | 'locationAddress'
> &
  Partial<Pick<JobDetailFormState, 'postalCode' | 'city'>> {
  const fields = {
    remote: opening.remote,
    industry: opening.job_industry,
    jobHours: opening.job_hours,
    jobType: opening.job_type,
    experience: opening.years_of_experience,
    locationAddress: opening.location_address,
  };
  if (opening.remote) return fields;
  return { ...fields, postalCode: opening.postal_code, city: opening.city };
}

function JobList() {
  const { selectedOpeningIds, hookForm } = useJobPostingContext();
  const { setValue, getValues, watch } = hookForm;
  const validateBaseFields = useValidateBaseFields();
  const jobDetailsList = watch('jobDetails');
  const channels = watch('channels');
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  // A remote opening's address as it was last loaded, per opening, so a
  // refetch can tell an unsaved edit from a value another card's save changed.
  const loadedAddressRef = useRef<Record<string, HiringAddressValues>>({});

  // Only fetch job details for openings that are actually selected in at least
  // one channel's `openingBudgets`. Openings the user removed from every
  // channel are dropped here so we don't waste a request on them.
  const filteredSelectedOpeningIds = useMemo(() => {
    const inChannels = openingIdsInChannels(channels);
    return selectedOpeningIds.filter(id => inChannels.has(id));
  }, [selectedOpeningIds, channels]);

  const {
    data: { openings },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.jobPosting.jobDetails, filteredSelectedOpeningIds],
    queryFn: () =>
      SourcingService.postInternalApiSourcingOpeningsOpeningsByExternalIds({
        opening_external_ids: filteredSelectedOpeningIds,
      }),
  });

  // Collect contract IDs and Marketplace product IDs from the Vonq channels.
  const { contractIds, productIds } = useMemo(() => {
    const contracts: string[] = [];
    const products = new Set<string>();
    for (const channel of Object.values(channels ?? {})) {
      const details = channel.vonqDetails;
      if (details?.contractId) contracts.push(details.contractId);
      else if (details?.productId) products.add(details.productId);
    }
    return { contractIds: contracts, productIds: [...products] };
  }, [channels]);

  // One read for the contracts, one per Marketplace product. Hire answers a
  // product read only with `source-vonq-direct-apply` on; a refused or failed
  // read yields no definition, so the step renders as it does without it.
  const defLists = useSuspenseQueries({
    queries: [
      {
        // Call the API only when there are contract IDs.
        queryKey: [...queryKeys.jobPosting.postingRequirements, contractIds],
        queryFn: async () =>
          contractIds.length > 0
            ? (
                await SourcingService.getInternalApiSourcingVonqContractsPostingRequirements(
                  contractIds,
                )
              ).map(contractDef)
            : [],
      },
      ...productIds.map(productId => ({
        queryKey: [
          ...queryKeys.jobPosting.postingRequirements,
          'product',
          productId,
        ],
        queryFn: async () => {
          try {
            return [
              productDef(
                productId,
                await SourcingService.getInternalApiSourcingVonqProductsPostingRequirements(
                  productId,
                ),
              ),
            ];
          } catch {
            return [];
          }
        },
      })),
    ],
    combine: combineDefs,
  });

  const postingRequirements = useMemo(() => defLists.flat(), [defLists]);

  /**
   * Reconcile the `jobDetails` form state with the latest `openings` payload
   * whenever it changes (initial mount and every successful refetch).
   * Also initialises `vonqPostingRequirements` for any contract/field pairs
   * that don't yet have a value, preserving any user edits already made.
   */
  useEffect(() => {
    if (openings.length === 0) return;

    const existingJobDetails = getValues('jobDetails');

    const next = openings.reduce<Record<string, JobDetailFormState>>(
      (acc, opening) => {
        const existing = existingJobDetails?.[opening.external_id];
        const serverFields = serverManagedFields(opening);

        // Determine which boards apply to this opening (i.e. the opening
        // appears in the channel's openingBudgets).
        const applicableChannels = Object.values(channels ?? {}).filter(
          ch =>
            ch.vonqDetails != null &&
            opening.external_id in (ch.openingBudgets ?? {}),
        );
        const applicableKeys = applicableChannels.map(ch =>
          requirementsKeyOf(ch.vonqDetails),
        );

        // Build / merge vonqPostingRequirements: preserve existing values,
        // fill in empty strings for any new fields.
        const existingRequirements = existing?.vonqPostingRequirements ?? {};
        const mergedRequirements = { ...existingRequirements };

        for (const channel of applicableChannels) {
          const key = requirementsKeyOf(channel.vonqDetails);
          if (key === null) continue;
          const def = postingRequirements.find(r => r.key === key);
          if (channel.vonqDetails?.contractId == null && !def) continue;
          const contractFields = def?.posting_requirements ?? [];

          const existingContractValues = mergedRequirements[key] ?? {};
          const merged = { ...existingContractValues };
          for (const field of contractFields) {
            if (!(field.name in merged)) {
              merged[field.name] = field.type === 'MULTIPLE' ? [] : '';
            }
          }
          mergedRequirements[key] = merged;
        }

        // Definitions for this opening's applicable contracts — stored in
        // form state so the Zod superRefine can validate required fields
        // without needing any reactive state outside of RHF.
        const vonqPostingRequirementsDefs = postingRequirements.filter(r =>
          applicableKeys.includes(r.key),
        );

        const fetchedAddress = hiringAddressOf(opening);
        const refreshedAddress =
          existing && opening.remote
            ? refreshUntouchedAddress(
                existing,
                loadedAddressRef.current[opening.external_id],
                fetchedAddress,
              )
            : {};
        loadedAddressRef.current[opening.external_id] = fetchedAddress;

        const base: JobDetailFormState = existing
          ? {
              ...existing,
              ...serverFields,
              ...refreshedAddress,
              vonqPostingRequirements: mergedRequirements,
              vonqPostingRequirementsDefs,
            }
          : {
              postalCode: opening.postal_code,
              city: opening.city,
              ...serverFields,
              slug: opening.slug,
              title: opening.title,
              description: opening.job_description ?? '',
              stateCode: opening.state_code ?? undefined,
              streetAddress: opening.street_address ?? undefined,
              isExpanded: openings.length === 1,
              vonqPostingRequirements: mergedRequirements,
              vonqPostingRequirementsDefs,
            };

        return { ...acc, [opening.external_id]: base };
      },
      // Preserve any orphaned entries already in form state (e.g. an opening
      // removed via "Remove from posting" — UI still filters those out at render time).
      { ...existingJobDetails },
    );

    setValue('jobDetails', next, { shouldValidate: false });
  }, [openings, postingRequirements, channels, setValue, getValues]);

  /**
   * Trigger validation for base (common) fields whenever the server data
   * changes — intentionally excludes `vonqPostingRequirements` so that
   * posting-requirement errors don't surface until the user visits that section.
   * Does not run on `channels` changes to avoid re-validating on every keystroke.
   */
  useEffect(() => {
    if (openings.length === 0) return;

    void validateBaseFields();
  }, [openings, postingRequirements, validateBaseFields]);

  /** Filter the openings to remove the ones that are deleted from the form state */
  const filteredOpenings = useMemo(() => {
    return openings.filter(opening =>
      Object.keys(jobDetailsList ?? {}).includes(opening.external_id),
    );
  }, [openings, jobDetailsList]);

  return (
    <div className="flex w-full flex-col gap-3">
      {showRefreshBanner && (
        <RefreshBanner onClose={() => setShowRefreshBanner(false)} />
      )}

      {filteredOpenings.map(opening => (
        <JobCard
          key={opening.external_id}
          opening={opening}
          postingRequirements={postingRequirements}
          onRequestRefreshBanner={() => setShowRefreshBanner(true)}
        />
      ))}
    </div>
  );
}

export function JobDetails() {
  const { selectedOpeningIds } = useJobPostingContext();

  return (
    <Suspense
      fallback={
        <>
          {_range(selectedOpeningIds.length).map(index => (
            <SummaryView.Skeleton key={index} />
          ))}
        </>
      }
    >
      <JobList />
    </Suspense>
  );
}
